import React, { useState, useContext, useEffect } from "react"
import ClientNotification from "../client/ClientNotification"
import { Button } from "../ui/button"
import { SubscriptionContext } from "../subscription/SubscriptionContext"
import { UserCtx } from "../../context/userContext"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useAuthModal } from "../auth/use-auth-modal"

export const planFeatures = [
  "Integrated Scheduling and billing",
  "Custom Coaching Offers",
  "Personalized Zoee website",
  "Secure client portal",
  "SMS messaging",
  "File sharing",
  "Client testimonials",
  "Digital notetaking system",
  "Network of coaches and community members",
  "Access to Coach Market Listing",
  "Member only events and perks",
  "Priority Zoee team support",
]

type Props = {
  planType: "annual" | "monthly" | "freeTrial"
  handleChoosePlanClick: (planType: string) => void
}

const PricingPlanCard: React.FC<Props> = ({
  planType,
  handleChoosePlanClick,
}) => {
  const { user } = useContext(UserCtx)
  const { subscription } = useContext(SubscriptionContext)
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false)
  const [clientNotification, setClientNotification] = useState(false)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const { showRegister } = useAuthModal()
  const [searchParams, setSearchParams] = useSearchParams()
  const register = searchParams.get("register")
  const coachregister = searchParams.get("coachregister")
  const navigate = useNavigate()

  const getPrice = () => {
    if (planType === "annual") return "$41/mo"
    if (planType === "monthly") return "$49/mo"
    return "$0/mo"
  }

  // getPriceWithStrike - use for promo deals
  const getPriceWithStrike = () => {
    if (planType === "annual") {
      return <p className="text-sm text-gray-600 line-through">$41/mo</p>
    }

    if (planType === "monthly") {
      return <p className="text-sm text-gray-600 line-through">$49/mo</p>
    }

    return <p className="invisible text-sm text-gray-600 line-through">$0/mo</p>
  }

  // promotionPrice - use for promo deals
  const promotionPrice = () => {
    if (planType === "annual") {
      return (
        <span>
          <span className="text-blurple font-semibold">$21.50</span>/mo
        </span>
      )
    }

    if (planType === "monthly") {
      return (
        <span>
          <span className="text-blurple font-semibold">$24.50</span>/mo
        </span>
      )
    }

    return <span>$0/mo</span>
  }

  const getStrikethroughPrice = () => {
    return <span style={{ visibility: "hidden" }}>freeTrial</span>
  }

  const getBillingCycleText = () => {
    if (planType === "annual") {
      return (
        // <p className="text-sm text-gray-600">
        //   Billed annually at <span className="line-through">$490</span>{" "}
        //   <span className="font-semibold text-black">$245</span>
        // </p>
        // Uncomment above & comment out below for future promo codes.
        <p className="text-sm text-gray-600">
          Billed annually at
          <span className="font-semibold text-black"> $490</span>
        </p>
      )
    }

    if (planType === "monthly") {
      return <span style={{ visibility: "hidden" }}>Monthly</span>
    }

    return <span style={{ visibility: "hidden" }}>freeTrial</span>
  }

  const promoDetails = () => {
    if (planType !== "freeTrial")
      return (
        <span className="italic">
          *Applicable to new or returning users only.
          <br />
        </span>
      )
    return (
      <span style={{ visibility: "hidden" }}>
        *Applicable to new or returning users only.
        <br />
      </span>
    )
  }

  const determineButtonVariant = () => {
    if (user && !subscription && planType === "freeTrial") return "disable"
    if (subscription?.recurring_interval === planType) return "disable"
    if (planType === "freeTrial") return "outline"
    return "default"
  }

  const determineButtonText = () => {
    if (!user) return "Get Started"
    if (user && !subscription && planType === "freeTrial") return "Current Plan"
    if (user && subscription?.recurring_interval === planType)
      return "Current Plan"
    return "Get Started"
  }

  const handleButtonClick = () => {
    // Use below for future promo deals
    //  BLITZ100 should only apply to first 100 users who uses this promo code.
    localStorage.setItem("referrerPromoCode", "BLITZ100")
    setShowMobileNav(false)

    if (user) {
      if (user.activeProfile === "coach") {
        navigate("/coach/subscription/plans", { state: { planType } })
      } else {
        setClientNotification(false)
        setTimeout(() => {
          setClientNotification(true)
        }, 0)
      }
    } else {
      showRegister({
        accountType: "coach",
        onSuccess: () => {
          navigate("/coach/subscription/plans", { state: { planType } })
        },
      })
    }
  }

  const openCreateAccountModal = () => {
    setModalOpen(true)
    showRegister()

    searchParams.set("register", "true")
    setSearchParams(searchParams)
  }

  useEffect(() => {
    if (!modalOpen) {
      searchParams.delete("register")
      setSearchParams(searchParams)
    }
  }, [modalOpen])

  useEffect(() => {
    if (coachregister === "true") {
      searchParams.set("register", "coach")
      searchParams.delete("coachregister")
    }

    if (register === "true") {
      openCreateAccountModal()
    } else if (register === "coach") {
      setShowMobileNav(false)
      showRegister({ accountType: "coach" })
    }
  }, [register, coachregister])

  return (
    <>
      {clientNotification && (
        <ClientNotification showNotification={clientNotification} />
      )}{" "}
      <div
        className={`sm:w-[406px] h-full flex flex-col rounded-[32px] ${
          planType === "freeTrial"
            ? "main-shadow bg-white"
            : "scale-[1.02] border-blue-500 shadow-xl hover:shadow-2xl hover:scale-[1.03] bg-gradient-to-br from-white via-[#e6f0ff] to-[#d0e0ff]"
        }`}
      >
        <div
          className={`w-full h-[35px] ${
            planType === "annual"
              ? "bg-blurple"
              : planType === "monthly" || planType === "freeTrial"
              ? "bg-black"
              : "bg-gray-400"
          } rounded-t-[32px] flex items-center justify-center text-[white] font-bold`}
        >
          {/* Use below for future promos */}
          {/* {planType !== "freeTrial" && "Save 50% – Limited Time Only!"} */}
        </div>
        <div className="pb-[32px] px-8 py-10 pt-[24px]">
          <div>
            <h2 className="text-black text-2xl font-semibold font-['Beausite Classic']">
              {planType === "annual"
                ? "Annual Pro"
                : planType === "monthly"
                ? "Monthly Pro"
                : "Free Trial"}
            </h2>
            <p className="self-stretch text-[#7d878f] text-base font-normal font-['Beausite Classic'] leading-snug">
              {planType === "freeTrial"
                ? "For coaches who want to try out Zoee"
                : "Zoee: One login, infinite possibilities"}
            </p>
            <div className="flex font-bold items-start gap-[4px] leading-none">
              <h4 className="text-center text-black text-[42px] font-semibold font-['Beausite Classic']">
                {/* {promotionPrice()} */}
                {/* Uncomment above & comment out below for future promo deals */}
                {getPrice()}
              </h4>
            </div>
            {/* Use below for future promo */}
            {/* <div className="flex items-start gap-[4px] leading-none">
              {getPriceWithStrike()}
            </div> */}
            <div className="mb-0">
              <p className="text-[18px] text-graySlate line-through">
                {getStrikethroughPrice()}
              </p>
              <p className="text-base text-graySlate">
                {getBillingCycleText()}
              </p>
            </div>
            {/* Use below for future promos that applies to new or returning users only */}
            {/* <div className="text-base text-graySlate ">
              <p>{promoDetails()}</p>
            </div> */}
          </div>
          <div className="flex flex-col gap-[12px]">
            <Button
              size="lg"
              variant={determineButtonVariant()}
              className="mt-[4px] md:mt-[20px] mx-[8px] md:mx-0 text-md"
              onClick={handleButtonClick}
              disabled={
                determineButtonVariant() === "disable" ||
                (subscription && planType === "freeTrial")
              }
            >
              {determineButtonText()}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default PricingPlanCard
